/**
 * useThemeStore — 테마/레이아웃/프리셋/스킨 Zustand 스토어.
 *
 * 【역할】
 * - light/dark 모드 토글
 * - side/top 레이아웃 전환
 * - 7개 컬러 프리셋 (default/ocean/forest/sunset/violet/rose/slate) + custom
 * - 디자인 스킨 (classic/soft/vivid/mono/neo) — primaryColor/다크모드와 직교 축
 *
 * 【State】
 *   - mode: ThemeMode — 다크/라이트
 *   - primaryColor: string — Ant Design colorPrimary 토큰
 *   - layout: LayoutMode — side(사이드바) / top(상단 메뉴)
 *   - compact: boolean — antd 컴팩트 알고리즘 토글
 *   - fontSize: number — 기본 폰트 크기(px)
 *   - preset: ThemePresetKey — 7개 컬러 프리셋 또는 'custom'
 *   - skin: ThemeSkinKey — classic/soft/vivid/mono/neo 디자인 스킨
 *
 * 【Actions】
 *   - setMode(m) — 다크/라이트 전환 + body[data-theme-mode] 갱신
 *   - setPrimaryColor(c) — 사용자 지정 색 → preset 자동 'custom' 전환
 *   - setLayout(l) / setCompact(c) / setFontSize(n) — 단순 set + persist
 *   - applyPreset(key) — 프리셋 선택 시 primaryColor 동시 갱신
 *   - setSkin(k) — 스킨 변경 + body[data-skin] 갱신 (CSS overlay 트리거)
 *
 * 【부수효과】
 *   - localStorage.setItem('fw.theme', JSON) — 전체 상태 직렬화
 *   - localStorage.setItem('fw.themeMode', 'light'|'dark') — 깜빡임 방지용 별도 키
 *   - document.body.dataset.themeMode / .skin — CSS overlay 활성화 트리거
 *
 * 【Ant Design 연동】
 * - ConfigProvider.theme.token.colorPrimary 를 프리셋에 따라 업데이트
 * - 스킨의 token 오버라이드는 useTheme 에서 합산
 *
 * 【사용처】 useTheme / ThemeSwitcher / AppLayout / index.html 인라인 스크립트(깜빡임 방지).
 * 【포팅 원본】 vue-spring-fw `useThemeStore.ts` + `ThemeSwitcher.vue` (Pinia → Zustand 전환).
 */
import { create } from 'zustand'
import type { ThemeSkinKey } from '@/design-skins'

/** 다크/라이트 토글값. */
export type ThemeMode = 'light' | 'dark'
/** 레이아웃 모드 — 사이드바형 / 상단 메뉴형. */
export type LayoutMode = 'side' | 'top'
/** 7개 사전 정의 컬러 프리셋 + 사용자 커스텀. */
export type ThemePresetKey =
  | 'default'
  | 'ocean'
  | 'forest'
  | 'sunset'
  | 'violet'
  | 'rose'
  | 'slate'
  | 'custom'

/** 컬러 프리셋 정의 1건. */
export interface ThemePresetDef {
  /** 프리셋 식별자. */
  key: ThemePresetKey
  /** UI 표시명 (한글). */
  label: string
  /** Ant Design colorPrimary 토큰값. */
  primaryColor: string
  /** optional extra token overrides for this preset */
  extraToken?: Record<string, unknown>
}

export const THEME_PRESETS: Record<Exclude<ThemePresetKey, 'custom'>, ThemePresetDef> = {
  default: { key: 'default', label: '기본 (Blue)', primaryColor: '#1677ff' },
  ocean: {
    key: 'ocean',
    label: '오션 (Cyan)',
    primaryColor: '#13c2c2',
    extraToken: { borderRadius: 8 }
  },
  forest: {
    key: 'forest',
    label: '포레스트',
    primaryColor: '#52c41a',
    extraToken: { borderRadius: 6 }
  },
  sunset: {
    key: 'sunset',
    label: '선셋',
    primaryColor: '#fa8c16',
    extraToken: { borderRadius: 10 }
  },
  violet: {
    key: 'violet',
    label: '바이올렛',
    primaryColor: '#722ed1',
    extraToken: { borderRadius: 8 }
  },
  rose: { key: 'rose', label: '로즈', primaryColor: '#eb2f96', extraToken: { borderRadius: 12 } },
  slate: {
    key: 'slate',
    label: '슬레이트',
    primaryColor: '#595959',
    extraToken: { borderRadius: 4 }
  }
}

interface ThemeState {
  /** 다크/라이트. */
  mode: ThemeMode
  /** 현재 colorPrimary (preset 의 색 또는 사용자 지정 hex). */
  primaryColor: string
  /** 사이드바형/상단형 레이아웃. */
  layout: LayoutMode
  /** antd compact 알고리즘 활성. */
  compact: boolean
  /** 기본 폰트 크기(px). */
  fontSize: number
  /** 컬러 프리셋 키 또는 'custom'. */
  preset: ThemePresetKey
  /** 디자인 스킨 키. */
  skin: ThemeSkinKey
  /** mode 전환 + body data-theme-mode 갱신 + persist. */
  setMode: (m: ThemeMode) => void
  /** primaryColor 직접 지정 — preset 은 자동 'custom'. */
  setPrimaryColor: (c: string) => void
  /** layout 전환. */
  setLayout: (l: LayoutMode) => void
  /** compact 토글. */
  setCompact: (c: boolean) => void
  /** 폰트 크기 변경. */
  setFontSize: (n: number) => void
  /** preset 적용 — primaryColor 동시 갱신. 'custom' 도 허용. */
  applyPreset: (key: ThemePresetKey) => void
  /** skin 적용 + body data-skin 갱신 (잘못된 키는 무시). */
  setSkin: (k: ThemeSkinKey) => void
}

const STORAGE_KEY = 'fw.theme'
const MODE_KEY = 'fw.themeMode'

const VALID_SKINS: ThemeSkinKey[] = ['classic', 'soft', 'vivid', 'mono', 'neo']

/** localStorage 직렬화 스냅샷 — ThemeState 의 데이터 필드만 추림. */
interface Persisted {
  mode: ThemeMode
  primaryColor: string
  layout: LayoutMode
  compact: boolean
  fontSize: number
  preset: ThemePresetKey
  skin: ThemeSkinKey
}

/**
 * localStorage 에서 테마 스냅샷 복원.
 * - 'fw.themeMode' 가 명시되어 있으면 그 값을 우선 (깜빡임 방지 인라인 스크립트와 동기화)
 * - skin 필드 부재(구버전 사용자)는 'classic' 폴백
 * - 손상/미존재/throw 시 기본값 반환 (절대 throw 하지 않음)
 */
function load(): Persisted {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
    const parsed: Partial<Persisted> = raw ? JSON.parse(raw) : {}
    const explicit =
      typeof localStorage !== 'undefined'
        ? (localStorage.getItem(MODE_KEY) as ThemeMode | null)
        : null
    const preset = (parsed.preset ?? 'default') as ThemePresetKey
    const presetColor = preset !== 'custom' ? THEME_PRESETS[preset]?.primaryColor : undefined
    // skin 필드: 기존 사용자(필드 부재)는 'classic' 폴백 — 하위 호환 보장
    const skinRaw = (parsed.skin ?? 'classic') as ThemeSkinKey
    const skin: ThemeSkinKey = VALID_SKINS.includes(skinRaw) ? skinRaw : 'classic'
    return {
      mode: explicit === 'dark' || explicit === 'light' ? explicit : (parsed.mode ?? 'light'),
      primaryColor: parsed.primaryColor ?? presetColor ?? '#1677ff',
      layout: parsed.layout === 'top' ? 'top' : 'side',
      compact: parsed.compact === true,
      fontSize: typeof parsed.fontSize === 'number' ? parsed.fontSize : 14,
      preset,
      skin
    }
  } catch {
    return {
      mode: 'light',
      primaryColor: '#1677ff',
      layout: 'side',
      compact: false,
      fontSize: 14,
      preset: 'default',
      skin: 'classic'
    }
  }
}

/** 스냅샷을 localStorage 두 키('fw.theme', 'fw.themeMode')에 영속화. */
function save(state: Persisted) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    localStorage.setItem(MODE_KEY, state.mode)
  } catch {
    /* noop */
  }
}

/** body[data-theme-mode] 갱신 — CSS dark mode overlay 트리거. SSR 안전. */
function applyBodyAttr(mode: ThemeMode) {
  if (typeof document !== 'undefined' && document.body) {
    document.body.dataset.themeMode = mode
  }
}

/** body[data-skin] 갱신 — 디자인 스킨 CSS overlay 트리거. SSR 안전. */
function applySkinAttr(skin: ThemeSkinKey) {
  if (typeof document !== 'undefined' && document.body) {
    document.body.dataset.skin = skin
  }
}

const initial = load()
applyBodyAttr(initial.mode)
applySkinAttr(initial.skin)

export const useThemeStore = create<ThemeState>((set, get) => {
  /** 현재 store 상태를 Persisted 스냅샷으로 캡처 (각 setter 의 save 호출에 사용). */
  const snapshot = (): Persisted => ({
    mode: get().mode,
    primaryColor: get().primaryColor,
    layout: get().layout,
    compact: get().compact,
    fontSize: get().fontSize,
    preset: get().preset,
    skin: get().skin
  })
  return {
    mode: initial.mode,
    primaryColor: initial.primaryColor,
    layout: initial.layout,
    compact: initial.compact,
    fontSize: initial.fontSize,
    preset: initial.preset,
    skin: initial.skin,
    setMode: (mode) => {
      set({ mode })
      save({ ...snapshot(), mode })
      applyBodyAttr(mode)
    },
    setPrimaryColor: (primaryColor) => {
      set({ primaryColor, preset: 'custom' })
      save({ ...snapshot(), primaryColor, preset: 'custom' })
    },
    setLayout: (layout) => {
      set({ layout })
      save({ ...snapshot(), layout })
    },
    setCompact: (compact) => {
      set({ compact })
      save({ ...snapshot(), compact })
    },
    setFontSize: (fontSize) => {
      set({ fontSize })
      save({ ...snapshot(), fontSize })
    },
    applyPreset: (key) => {
      if (key === 'custom') {
        set({ preset: 'custom' })
        save({ ...snapshot(), preset: 'custom' })
        return
      }
      const def = THEME_PRESETS[key]
      if (!def) return
      set({ preset: key, primaryColor: def.primaryColor })
      save({ ...snapshot(), preset: key, primaryColor: def.primaryColor })
    },
    setSkin: (skin) => {
      if (!VALID_SKINS.includes(skin)) return
      set({ skin })
      save({ ...snapshot(), skin })
      applySkinAttr(skin)
    }
  }
})
